<script lang="ts">
	import type CriteoProvider from '$lib/components/third-parties/criteo/store.svelte';
	import type GoogleAnalyticsProvider from '$lib/components/third-parties/google/analytics/store.svelte';
	import type KwankoProvider from '$lib/components/third-parties/kwanko/store.svelte';
	import type TikTokAdsProvider from '$lib/components/third-parties/tiktok-ads/store.svelte';

	import { datadogRum } from '@datadog/browser-rum';
	import { page } from '$app/state';
	import {
		brandsSlugs,
		getNameWithoutBrand,
		mediaUrl,
		thumbnailUrl,
		translateOptionTitle,
	} from '$lib';
	import { keepInView } from '$lib/components/attachments';
	import BigButton from '$lib/components/BigButton.svelte';
	import Breadcrumbs from '$lib/components/Breadcrumbs.svelte';
	import CartDrawer from '$lib/components/cart/CartDrawer.svelte';
	import CartProvider, { type ICartItemOption } from '$lib/components/cart/store.svelte';
	import Drawer from '$lib/components/Drawer.svelte';
	import Lazy from '$lib/components/Lazy.svelte';
	import Details from '$lib/components/product/Details.svelte';
	import FullScreenImageCarousel from '$lib/components/product/FullScreenImageCarousel.svelte';
	import ImageCarousel from '$lib/components/product/ImageCarousel.svelte';
	import OptionsSelector from '$lib/components/product/OptionsSelector.svelte';
	import Picto24h from '$lib/components/product/Picto24h.svelte';
	import ProductAttributes from '$lib/components/product/ProductAttributes.svelte';
	import ProductCarousel from '$lib/components/product/ProductCarousel.svelte';
	import ProductPrice from '$lib/components/product/ProductPrice.svelte';
	import SiblingSelector from '$lib/components/product/SiblingSelector.svelte';
	import VariationQuantitySelector from '$lib/components/product/VariationQuantitySelector.svelte';
	import VariationSelector from '$lib/components/product/VariationSelector.svelte';
	import MetaAdsProvider from '$lib/components/third-parties/meta-ads/store.svelte';
	import PayPalPayLaterNotice from '$lib/components/third-parties/paypal/PayPalPayLaterNotice.svelte';
	import ThumbnailImage from '$lib/components/ThumbnailImage.svelte';
	import Title from '$lib/components/Title.svelte';
	import { m } from '$lib/paraglide/messages.js';
	import { getContext, untrack } from 'svelte';
	import { SvelteMap } from 'svelte/reactivity';

	import { fetchBestSellers, fetchComplementary, type ProductPageData } from './page.product';

	datadogRum.setViewName('/product');

	const { data }: { data: ProductPageData } = $props();
	const cart = getContext<CartProvider>('cart');
	const metaAds = getContext<MetaAdsProvider>('metaAds');
	const kwanko = getContext<KwankoProvider>('kwanko');
	const criteo = getContext<CriteoProvider>('criteo');
	const tiktokAds = getContext<TikTokAdsProvider>('tiktokAds');
	const googleAnalytics = getContext<GoogleAnalyticsProvider>('googleAnalytics');

	const images = $derived([...new Set(data.product.data.images)]);

	let variationSelected = $derived(data.selectedVariant?.variation);
	const selectedVariant = $derived(
		variationSelected !== undefined
			? data.product.variants.find((v) => v.variation === variationSelected)
			: data.selectedVariant,
	);
	const hasAvailableVariants = $derived(
		data.product.variants.some((variant) => !variant.is_sold_out),
	);
	const cheapestVariant = $derived(
		hasAvailableVariants
			? data.product.variants
					.filter((v) => !v.is_sold_out)
					.reduce((acc, v) =>
						(acc.special_price ?? acc.price!) < (v.special_price ?? v.price!) ? acc : v,
					)
			: null,
	);
	const variantSelectedOrCheapest = $derived(selectedVariant ?? cheapestVariant);
	const hasVariation = $derived(
		data.product.variants.length > 1 || data.product.variants[0].variation !== undefined,
	);
	const hasMultipleQuantitySalesPack = $derived(
		selectedVariant &&
			data.product.data.quantity_sales_pack &&
			data.product.data.quantity_sales_pack > 1 &&
			!selectedVariant.in_stock,
	);
	const isAvailable = $derived(selectedVariant && !selectedVariant.is_sold_out);

	let selectedOptions = $derived(data.selectedOptions ?? new SvelteMap());
	const optionsPrice = $derived(
		Array.from(selectedOptions.entries()).reduce(
			(prev, curr) => (prev += curr[0].price + (typeof curr[1] === 'object' ? curr[1].price : 0)),
			0,
		),
	);

	const maxImagesToShow = 7;
	let showAllImages = $state(false);

	let cartDrawer: CartDrawer | undefined = $state();
	let currentProductQty = $derived(data.product.data.quantity_sales_pack ?? 1);

	function handleCartSubmit(
		e: SubmitEvent & {
			currentTarget: EventTarget & HTMLFormElement;
		},
	) {
		e.preventDefault();

		if (!selectedVariant) {
			return;
		}

		const productQty = Number(e.currentTarget.qty.value);

		cart.addItemQty(
			data.product,
			selectedVariant.data.sku,
			Array.from(selectedOptions.entries()).map(
				([option, value]) =>
					({
						id: option.id,
						title: translateOptionTitle(page.data.store.language, option),
						value_id: typeof value === 'object' ? value.id : null,
						value:
							typeof value === 'object'
								? translateOptionTitle(page.data.store.language, value)
								: value,
					}) satisfies ICartItemOption,
			),
			optionsPrice,
			productQty,
		);
		cartDrawer?.open({
			name: data.product.data.name,
			sku: selectedVariant.data.sku,
			quantity: productQty,
			image: images[0],
		});

		metaAds?.trackAddToCart(selectedVariant!, productQty);
		tiktokAds?.trackAddToCart(selectedVariant!, data.product.data.name, productQty);
		googleAnalytics?.trackAddToCart(data.product, selectedVariant!, productQty);

		currentProductQty = data.product.data.quantity_sales_pack ?? 1;
	}

	let fullScreenDialog: FullScreenImageCarousel | undefined;
	function handleImageClick(index: number) {
		fullScreenDialog?.show(index);
	}

	let attachmentState = $state({ inView: false });
	let showSizeDrawer = $state(false);
	let showOptionsDrawer = $state(false);

	$effect(() => {
		// When selected variant change (or product)
		if (selectedVariant) {
			metaAds?.trackViewContentVariant(data.product, selectedVariant);
		}
	});

	$effect(() => {
		// When product change only once with 'cheapestVariant'
		if (!cheapestVariant) return;

		metaAds?.trackViewContent(
			data.product,
			untrack(() => cheapestVariant.special_price ?? cheapestVariant.price!),
		);
		tiktokAds?.trackViewContent(
			data.product,
			untrack(() => cheapestVariant.special_price ?? cheapestVariant.price!),
		);
		googleAnalytics?.trackViewItem(
			data.product,
			untrack(() => cheapestVariant),
		);

		kwanko?.trackProduct(data.product);
		criteo?.trackViewItem([data.product.data.sku, ...data.product.variants.map((v) => v.data.sku)]);
	});
</script>

<svelte:head>
	{#if data.jsonLd}
		<!-- eslint-disable-next-line @typescript-eslint/no-unused-expressions -->
		{@html '<script type="application/ld+json">' + JSON.stringify(data.jsonLd) + '</script>'}
	{/if}

	{#if images?.length}
		<meta
			property="og:image"
			content={thumbnailUrl(page.data.store.media_base_url, `product/${images[0]}`, 300)}
		/>
	{/if}
</svelte:head>

<FullScreenImageCarousel bind:this={fullScreenDialog} {images} />

<CartDrawer bind:this={cartDrawer} />

<div class="full-container mx-auto mb-4">
	<Breadcrumbs overrideCurrent={data.product.data.name} retrieveFromState />
</div>

<div class="full-container mx-auto flex flex-col gap-20">
	<div class="flex flex-col gap-10 md:flex-row" {@attach keepInView(attachmentState)}>
		<div class="md:basis-7/12">
			<div class="mb-2 block md:hidden">
				{#if images.length > 0}
					<ImageCarousel {images} onclick={handleImageClick} />
				{:else}
					<div class="h-96 w-full bg-neutral-100"></div>
				{/if}
			</div>
			<div class="hidden grid-cols-2 gap-8 md:grid">
				{#if images.length > 0}
					{#each images.slice(0, showAllImages ? images.length : maxImagesToShow) as image, i (image)}
						<button
							type="button"
							class="bg-product cursor-zoom-in first:col-span-2"
							onclick={() => handleImageClick(i)}
						>
							<ThumbnailImage
								src="product/{image}"
								defaultSize={1200}
								class="w-full mix-blend-darken"
							/>
						</button>
					{/each}
				{:else}
					<div class="h-96 w-full bg-neutral-100 first:col-span-2"></div>
				{/if}
			</div>
			{#if images.length > maxImagesToShow}
				<div class="mt-8 hidden justify-center md:flex">
					<button
						class="flex cursor-pointer rounded-md border bg-white px-5 py-2 text-lg font-medium transition duration-300 hover:bg-neutral-400 hover:text-white"
						onclick={() => {
							showAllImages = !showAllImages;
						}}
					>
						{#if showAllImages}
							{m.show_less()}
						{:else}
							{m.show_more()}
						{/if}
						<span
							class={[
								'icon-[mdi--keyboard-arrow-up] h-7 w-7 transition',
								{ 'rotate-0': showAllImages, 'rotate-180': !showAllImages },
							]}
						></span>
					</button>
				</div>
			{/if}
		</div>
		<div
			class="flex basis-5/12 flex-col gap-4 md:sticky md:top-[var(--header-total-height)] md:self-start"
		>
			<div>
				<a
					href="/{brandsSlugs[page.data.store.language]}/{data.product.data.brand.slug}"
					class="text-2xl font-light tracking-[.25em] text-neutral-600 uppercase"
				>
					{#if data.product.data.brand.logo}
						<img
							src={mediaUrl(
								page.data.store.media_base_url,
								`wysiwyg/brands/logos/${data.product.data.brand.logo}`,
							)}
							alt={data.product.data.brand.name}
							class="my-3 h-7.5 w-auto"
						/>
					{:else}
						{data.product.data.brand.name}
					{/if}
				</a>

				<Title>
					{getNameWithoutBrand(data.product.data.name, data.product.data.brand.name)}
				</Title>
			</div>

			<PayPalPayLaterNotice
				price={(variantSelectedOrCheapest?.special_price ?? variantSelectedOrCheapest?.price ?? 0) +
					optionsPrice}
				quantity={currentProductQty}
			/>

			{#if variantSelectedOrCheapest}
				<div class="hidden md:block">
					<ProductPrice
						variant={variantSelectedOrCheapest}
						startingAt={!selectedVariant}
						{optionsPrice}
					/>
				</div>
			{/if}

			<div class="prose prose-sm">
				{@html data.product.data.short_description}
			</div>

			{#if data.product.siblings && data.product.siblings.length > 1}
				<SiblingSelector slug={data.product.data.slug} showAll siblings={data.product.siblings} />
			{/if}

			<div
				class="fixed right-0 bottom-0 left-0 z-5 rounded-t-xl bg-white px-2 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] shadow-[0_-8px_8px_rgba(0,0,0,0.1)] transition-transform md:relative md:p-0 md:shadow-none {attachmentState.inView
					? 'translate-y-0'
					: 'translate-y-[calc(100%+env(safe-area-inset-bottom))] md:translate-0'}"
			>
				<form onsubmit={handleCartSubmit} class="group flex grow flex-col gap-2">
					{#if hasAvailableVariants}
						{@const hasVariationAndOptions =
							hasVariation && data.product.options && data.product.options.length}
						<div
							class={[
								'grid items-stretch justify-between gap-2 md:hidden',
								hasVariationAndOptions ? 'grid-cols-2' : 'grid-cols-1',
							]}
						>
							{#if hasVariation}
								<button
									type="button"
									class="not:disabled:hover:border-neutral-400 not:disabled:hover:bg-neutral-100 relative basis-2/5 cursor-pointer overflow-hidden rounded border border-neutral-300 bg-white p-1 text-sm text-black transition-colors group-has-[:user-invalid]:border-red-600 disabled:border-dashed"
									disabled={!hasAvailableVariants || data.product.variants.length === 1}
									onclick={() => (showSizeDrawer = true)}
								>
									{variationSelected ?? m.select_size()}

									{#if selectedVariant && selectedVariant.in_stock && !selectedVariant.is_sold_out}
										<Picto24h
											class="-top-0.25! -right-0.25! rounded-tr text-[0.65rem] in-[label:hover]:bg-black"
										/>
									{/if}
								</button>
							{/if}
							{#if data.product.options && data.product.options.length}
								<button
									type="button"
									class="not:disabled:hover:border-neutral-400 not:disabled:hover:bg-neutral-100 relative basis-2/5 cursor-pointer overflow-hidden rounded border border-neutral-300 bg-white p-1 text-sm text-black transition-colors disabled:border-dashed"
									onclick={() => (showOptionsDrawer = true)}
								>
									{selectedOptions.size
										? m.selected_options({ count: selectedOptions.size })
										: m.select_options()}
								</button>
							{/if}
							{#if variantSelectedOrCheapest}
								<div
									class={[
										'flex flex-col items-center justify-center rounded border border-dashed border-neutral-300 p-1 text-xs text-neutral-600',
										hasVariation ? 'basis-3/5' : 'w-full',
										hasVariationAndOptions ? 'col-span-2' : '',
									]}
								>
									<ProductPrice
										variant={variantSelectedOrCheapest}
										startingAt={!selectedVariant}
										{optionsPrice}
									/>
								</div>
							{/if}
						</div>
						<div class="hidden md:block">
							{#if hasVariation}
								<VariationSelector
									variants={data.product.variants}
									bind:selectedVariation={variationSelected}
									sizeGuide={data.product.data.brand.size_guide}
								/>
							{/if}
							{#if data.product.options}
								<OptionsSelector options={data.product.options} bind:selectedOptions />
							{/if}
						</div>
						<div class="flex h-10 gap-2">
							<VariationQuantitySelector
								bind:value={currentProductQty}
								quantitySalesPack={selectedVariant?.in_stock
									? null
									: data.product.data.quantity_sales_pack}
								name="qty"
								disabled={!hasAvailableVariants}
							/>
							<BigButton type="submit" class="grow cursor-pointer text-xs md:text-sm">
								{m.add_to_cart()}
							</BigButton>
						</div>

						{#if hasMultipleQuantitySalesPack || isAvailable}
							{#snippet information_block(text: string, color: string, icon: string)}
								<div
									class="flex flex-auto items-center justify-center gap-x-1 py-0 md:justify-start md:py-1 {color}"
								>
									<span class="{icon} size-6 md:size-8"></span>
									<span class="text-xs md:text-sm"> {text}</span>
								</div>
							{/snippet}
							<div class="order-first flex flex-wrap gap-1 font-bold md:order-last">
								{#if selectedVariant?.data.seller !== 'Spacefoot'}
									{@render information_block(
										m.partner_offer(),
										'text-cyan-600',
										'icon-[mdi--star-circle-outline]',
									)}
								{/if}

								{#if hasMultipleQuantitySalesPack}
									{@render information_block(
										m.sold_by_qty({ qty: data.product.data.quantity_sales_pack ?? 1 }),
										'text-neutral-600',
										'icon-[mdi--checkbox-multiple-blank-circle]',
									)}
								{/if}

								{#if isAvailable && selectedVariant?.lead_time}
									{@render information_block(
										m.shipped_in({ days: selectedVariant.lead_time }),
										selectedVariant.lead_time === 1 ? 'text-green-600' : 'text-neutral-600',
										selectedVariant.lead_time === 1
											? 'icon-[mdi--truck-fast]'
											: 'icon-[mdi--truck]',
									)}
								{/if}
							</div>
						{/if}
					{:else}
						<div class="mb rounded-sm border border-red-100 bg-red-50 px-4 py-2 text-center">
							<p class="font-medium text-red-500">{m.product_unavailable()}</p>
							{#if hasVariation}
								<p class="mt-1 text-sm text-red-500">{m.all_variants_sold_out()}</p>
							{/if}
						</div>
					{/if}
				</form>
			</div>

			{#if data.product.data.description}
				<Details details={m.description()}>
					<div class="prose prose-sm mt-1">
						{@html data.product.data.description}
					</div>
				</Details>
			{/if}

			<ProductAttributes product={data.product} variant={selectedVariant} />
		</div>
	</div>
	{#key data.product.data.slug}
		<Lazy>
			{#await fetchComplementary(new URLSearchParams( { slug: data.product.data.slug }, )) then complementary}
				{#if complementary?.length}
					<ProductCarousel products={complementary}>
						{m.complementary_products()}
					</ProductCarousel>
				{/if}
			{/await}
		</Lazy>
	{/key}

	{#key data.product.data.slug}
		<Lazy>
			{#await fetchBestSellers(new URLSearchParams( { slug: data.product.data.slug }, )) then bestSellers}
				{#if bestSellers?.length}
					<ProductCarousel products={bestSellers}>
						{m.best_sellers()}
					</ProductCarousel>
				{/if}
			{/await}
		</Lazy>
	{/key}
</div>

<Drawer bind:open={showSizeDrawer}>
	{#snippet header()}
		<h2 class="text-2xl font-bold">{m.select_size()}</h2>
	{/snippet}

	<div class="flex grow flex-col gap-2 overflow-x-hidden">
		{#if hasAvailableVariants}
			{#if hasVariation}
				<VariationSelector
					variants={data.product.variants}
					bind:selectedVariation={variationSelected}
					sizeGuide={data.product.data.brand.size_guide}
					onchange={() => (showSizeDrawer = false)}
				/>
			{/if}
		{:else}
			<div class="mb-5 rounded-sm border border-red-200 bg-red-50 p-4 text-center">
				<p class="font-medium text-red-600">{m.product_unavailable()}</p>
				{#if hasVariation}
					<p class="mt-1 text-sm text-red-500">{m.all_variants_sold_out()}</p>
				{/if}
			</div>
		{/if}
	</div>
</Drawer>

<Drawer bind:open={showOptionsDrawer}>
	{#snippet header()}
		<h2 class="text-2xl font-bold">{m.select_options()}</h2>
	{/snippet}

	<div class="mb-3">
		{#if data.product.options && data.product.options.length}
			<OptionsSelector options={data.product.options} bind:selectedOptions />
		{/if}
	</div>

	<BigButton onclick={() => (showOptionsDrawer = false)}>{m.apply()}</BigButton>
</Drawer>
